import {
  DateTime,
  GraphicsDuckImage,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { PayoutState } from './PayoutState';
import { formatServerMoney, mapStrangeServerMoneyToServerMoney } from '@razz-team/utils';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { ApiPayout } from '../../../openapi';
import { usePayoutsInfiniteQuery } from '../../../api/payouts';
import { format } from 'date-fns';
import { groupBy } from 'lodash-es';

const getCorrectAmount = (payout: ApiPayout) => {
  // TODO: fix types
  if (payout.paidAmount && payout.totalAmount) {
    switch (payout.state) {
      case 'CREATED':
      case 'STARTED':
      case 'PAUSED':
        return `≈ ${formatServerMoney(mapStrangeServerMoneyToServerMoney(payout.totalAmount))}`;
      case 'COMPLETED':
      case 'CANCELED':
        return formatServerMoney(mapStrangeServerMoneyToServerMoney(payout.paidAmount));
      default:
        return formatServerMoney(mapStrangeServerMoneyToServerMoney(payout.totalAmount));
    }
  }
};

export function PayoutsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const payoutsQuery = usePayoutsInfiniteQuery();

  const payouts = useMemo(() => {
    const flat = payoutsQuery.data?.pages.flatMap((page) => page.payouts) ?? [];
    return groupBy(flat, (payout) => format(new Date(payout.createdAt), 'MMMM'));
  }, [payoutsQuery.data?.pages]);

  const handleClickPayout = useCallback(
    (payout: ApiPayout) => {
      navigate({
        to: `/account/payouts/$payoutId`,
        params: { payoutId: payout.id },
        search: { createdAt: payout.createdAt },
      });
    },
    [navigate],
  );

  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell width="20%">#</TableCell>
          <TableCell width="20%">{t('Transfers')}</TableCell>
          <TableCell width="20%">{t('Amount')}</TableCell>
          <TableCell width="20%">{t('Status')}</TableCell>
          <TableCell width="20%">{t('Date')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {payoutsQuery.isSuccess && payoutsQuery.data?.pages.length === 0 && (
          <TableRow>
            <TableCell variant="subhead" colSpan={5}>
              <Stack direction="column" alignItems="center">
                {__BRIGHTY__ ? <GraphicsDuckImage width={120} height={120} /> : null}
                <Typography color="text.secondary">{t('Nothing found')}</Typography>
              </Stack>
            </TableCell>
          </TableRow>
        )}
        {payoutsQuery.isPending && (
          <>
            <TableRow>
              <TableCell variant="subhead" colSpan={5}>
                <Skeleton variant="text" width={100} />
              </TableCell>
            </TableRow>
            {Array.from({ length: 3 }).map((_, rowIndex) => (
              <TableRow key={rowIndex} sx={{ height: '40px' }}>
                {Array.from({ length: 5 }).map((__, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="text" width={'50%'} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </>
        )}
        {Object.entries(payouts).map(([month, payoutsInMonth]: [string, ApiPayout[]]) => (
          <Fragment key={month}>
            <TableRow>
              <TableCell variant="subhead">{month}</TableCell>
            </TableRow>
            {payoutsInMonth.map((payout) => (
              <TableRow key={payout.id} variant="click" onClick={() => handleClickPayout(payout)}>
                <TableCell>{payout.name}</TableCell>
                <TableCell>{payout.totalTransfers}</TableCell>
                <TableCell>{getCorrectAmount(payout)}</TableCell>
                <TableCell>
                  <PayoutState payout={payout} />
                </TableCell>
                <TableCell>
                  <DateTime value={payout.createdAt} format="date" variant="caption" />
                </TableCell>
              </TableRow>
            ))}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}
